/** @format */

import { createSlice } from "@reduxjs/toolkit";
import {
	// Actions
	getPlatformFeeRevenue,
	getStripeAccountBalance,
	getStripeActivitySummary,
	// Types
	PlatformFeeRevenueResponse,
	StripeBalanceInfo,
	StripeActivitySummary,
} from "../actions/revenueAction";

// ==================== State Interface ====================

export interface AdminRevenueState {
	// Platform Fee Revenue
	feeRevenue: PlatformFeeRevenueResponse | null;
	isLoadingFeeRevenue: boolean;
	feeRevenueError: string | null;

	// Stripe Balance
	stripeBalance: StripeBalanceInfo | null;
	isLoadingStripeBalance: boolean;
	stripeBalanceError: string | null;

	// Stripe Activity
	stripeActivity: StripeActivitySummary | null;
	isLoadingStripeActivity: boolean;
	stripeActivityError: string | null;

	// General state
	isLoading: boolean;
	error: string | null;

	// Last refresh timestamp
	lastRefreshedAt: string | null;
}

// ==================== Initial State ====================

const initialState: AdminRevenueState = {
	// Platform Fee Revenue
	feeRevenue: null,
	isLoadingFeeRevenue: false,
	feeRevenueError: null,

	// Stripe Balance
	stripeBalance: null,
	isLoadingStripeBalance: false,
	stripeBalanceError: null,

	// Stripe Activity
	stripeActivity: null,
	isLoadingStripeActivity: false,
	stripeActivityError: null,

	// General state
	isLoading: false,
	error: null,

	// Last refresh timestamp
	lastRefreshedAt: null,
};

// ==================== Slice ====================

const adminRevenueSlice = createSlice({
	name: "adminRevenue",
	initialState,
	reducers: {
		// Clear all errors
		clearRevenueErrors: (state) => {
			state.error = null;
			state.feeRevenueError = null;
			state.stripeBalanceError = null;
			state.stripeActivityError = null;
		},
		// Clear fee revenue data
		clearFeeRevenue: (state) => {
			state.feeRevenue = null;
			state.feeRevenueError = null;
		},
		// Clear stripe balance
		clearStripeBalance: (state) => {
			state.stripeBalance = null;
			state.stripeBalanceError = null;
		},
		// Clear stripe activity
		clearStripeActivity: (state) => {
			state.stripeActivity = null;
			state.stripeActivityError = null;
		},
		// Clear all revenue data
		clearAllRevenueData: (state) => {
			state.feeRevenue = null;
			state.stripeBalance = null;
			state.stripeActivity = null;
			state.error = null;
			state.feeRevenueError = null;
			state.stripeBalanceError = null;
			state.stripeActivityError = null;
		},
	},
	extraReducers: (builder) => {
		// ==================== Get Platform Fee Revenue ====================
		builder
			.addCase(getPlatformFeeRevenue.pending, (state) => {
				state.isLoadingFeeRevenue = true;
				state.isLoading = true;
				state.feeRevenueError = null;
			})
			.addCase(getPlatformFeeRevenue.fulfilled, (state, action) => {
				state.isLoadingFeeRevenue = false;
				state.isLoading = false;
				state.feeRevenue = action.payload.data;
				state.lastRefreshedAt = new Date().toISOString();
			})
			.addCase(getPlatformFeeRevenue.rejected, (state, action) => {
				state.isLoadingFeeRevenue = false;
				state.isLoading = false;
				state.feeRevenueError = action.payload as string;
			});

		// ==================== Get Stripe Account Balance ====================
		builder
			.addCase(getStripeAccountBalance.pending, (state) => {
				state.isLoadingStripeBalance = true;
				state.stripeBalanceError = null;
			})
			.addCase(getStripeAccountBalance.fulfilled, (state, action) => {
				state.isLoadingStripeBalance = false;
				state.stripeBalance = action.payload.data;
			})
			.addCase(getStripeAccountBalance.rejected, (state, action) => {
				state.isLoadingStripeBalance = false;
				state.stripeBalanceError = action.payload as string;
			});

		// ==================== Get Stripe Activity Summary ====================
		builder
			.addCase(getStripeActivitySummary.pending, (state) => {
				state.isLoadingStripeActivity = true;
				state.stripeActivityError = null;
			})
			.addCase(getStripeActivitySummary.fulfilled, (state, action) => {
				state.isLoadingStripeActivity = false;
				state.stripeActivity = action.payload.data;
			})
			.addCase(getStripeActivitySummary.rejected, (state, action) => {
				state.isLoadingStripeActivity = false;
				state.stripeActivityError = action.payload as string;
			});
	},
});

// ==================== Export Actions ====================

export const {
	clearRevenueErrors,
	clearFeeRevenue,
	clearStripeBalance,
	clearStripeActivity,
	clearAllRevenueData,
} = adminRevenueSlice.actions;

// ==================== Export Reducer ====================

export default adminRevenueSlice.reducer;
